/**
 * Shared chapter audio: one MP3 + timings for both ayah play and full-surah play.
 * Mirrors Android QuranMediaController (chapter file + seek into verse windows).
 *
 * Contiguous ayah auto-advance MUST NOT seek (Android isContiguous) — only update
 * the active window/UI so the stream continues and the next opening is not replayed.
 */
import {
  fetchChapterAudioTimings,
  resolveActiveWordLocation,
  clearChapterAudioTimingsCache,
} from './chapterAudioTimings';
import {
  updateMediaSessionMetadata,
  setMediaSessionPlaybackState,
  setMediaSessionPositionState,
  bindMediaSessionHandlers,
  clearMediaSession,
} from './mediaSession';
import { scrollAyahIntoViewSettled } from './scrollAyahIntoView';

const SYNC_MS = 120;
const MEDIA_POS_THROTTLE_MS = 900;
const DEBUG =
  typeof window !== 'undefined' &&
  (window.localStorage?.getItem('DEBUG_CHAPTER_AUDIO') === '1' ||
    process.env.NODE_ENV === 'development');

function dbg(...args) {
  if (DEBUG) console.log('[ChapterAudio]', ...args);
}

/**
 * @param {object} deps
 * @param {(ayah: object|null) => void} deps.setCurrentlyPlayingAyah
 * @param {(word: string|null) => void} deps.setActiveWordLocation
 * @param {(ayahKey: string, duration: number) => void} deps.trackAyahPlayed
 * @param {() => number} deps.getLoopCount
 * @param {() => number|string} deps.getRecitationId
 */
export function createChapterAudioController(deps) {
  /** @type {HTMLAudioElement} */
  const audio = new Audio();
  audio.preload = 'auto';

  let loadedKey = ''; // `${recitationId}:${surahId}`
  /** @type {import('./chapterAudioTimings').ChapterAudioTimings|null} */
  let timings = null;
  let mode = null; // 'ayah' | 'chapter' | null
  let ayahKey = null;
  let nextAyahKey = null;
  let iteration = 0;
  let ayahEndMs = null;
  let playbackBlocked = false;
  let ayahStartMs = null;
  let syncTimer = null;
  let transitioning = false;
  /** @type {((playing: boolean) => void)|null} */
  let setUiPlaying = null;
  /** @type {Set<() => void>} */
  const listeners = new Set();

  // Seek coalescing — browsers fire many seeking/seeked + intermediate currentTime values
  let seekGen = 0;
  let seekSettleTimer = null;
  let isSeeking = false;
  /** @type {number|null} ms target from the latest scrubber/seekToMs call */
  let pendingSeekMs = null;
  let lastMediaPosAt = 0;
  let mediaHandlersBound = false;

  const notify = () => {
    syncMediaSessionUi();
    listeners.forEach((fn) => {
      try {
        fn(getSnapshot());
      } catch {
        /* ignore */
      }
    });
  };

  function refreshMediaMetadata() {
    const sid = loadedKey ? Number(loadedKey.split(':')[1]) : null;
    updateMediaSessionMetadata({
      surahId: sid,
      ayahKey,
      mode,
      artist: 'Quran Recitation',
    });
  }

  function syncMediaSessionUi({ forcePosition = false } = {}) {
    if (!mode) return;
    setMediaSessionPlaybackState(audio.paused ? 'paused' : 'playing');
    const now = Date.now();
    if (!forcePosition && now - lastMediaPosAt < MEDIA_POS_THROTTLE_MS && !audio.paused) {
      return;
    }
    lastMediaPosAt = now;
    const duration =
      (Number.isFinite(audio.duration) && audio.duration > 0
        ? audio.duration
        : (timings?.durationMs || 0) / 1000) || 0;
    setMediaSessionPositionState({
      duration,
      position: audio.currentTime || 0,
      playbackRate: audio.playbackRate || 1,
    });
  }

  function getSnapshot() {
    return {
      audio,
      timings,
      mode,
      ayahKey,
      surahId: loadedKey
        ? Number(loadedKey.split(':')[1])
        : null,
      loadedKey,
      isPaused: audio.paused,
      currentTime: audio.currentTime,
      duration: audio.duration || (timings?.durationMs ? timings.durationMs / 1000 : 0),
      iteration,
      isSeeking,
      playbackBlocked,
    };
  }

  function clearPlaybackBlocked() {
    if (!playbackBlocked) return;
    playbackBlocked = false;
    notify();
  }

  function isGestureBlockedError(err) {
    if (!err) return false;
    const name = err.name || '';
    const msg = String(err.message || err);
    return (
      name === 'NotAllowedError' ||
      name === 'AbortError' ||
      /not allowed|user didn't interact|user agent|autoplay/i.test(msg)
    );
  }

  /** @returns {Promise<void>} */
  async function safePlay() {
    try {
      await audio.play();
      clearPlaybackBlocked();
    } catch (err) {
      if (isGestureBlockedError(err)) {
        playbackBlocked = true;
        setMediaSessionPlaybackState('paused');
        notify();
        const blocked = new Error('Playback blocked until user taps play');
        blocked.name = 'NotAllowedError';
        blocked.cause = err;
        throw blocked;
      }
      throw err;
    }
  }

  function stopSync() {
    if (syncTimer) {
      clearInterval(syncTimer);
      syncTimer = null;
    }
  }

  function clearPlaybackState() {
    stopSync();
    mode = null;
    ayahKey = null;
    nextAyahKey = null;
    ayahEndMs = null;
    ayahStartMs = null;
    iteration = 0;
    transitioning = false;
    isSeeking = false;
    pendingSeekMs = null;
    playbackBlocked = false;
    if (setUiPlaying) {
      try {
        setUiPlaying(false);
      } catch {
        /* ignore */
      }
      setUiPlaying = null;
    }
    deps.setCurrentlyPlayingAyah(null);
    deps.setActiveWordLocation(null);
    clearMediaSession();
    notify();
  }

  function scrollToAyah(key, { instant = true } = {}) {
    if (!key) return;
    dbg('scrollToAyah', key, { instant, isSeeking });
    // Position ayah top above center; re-measure after tafsir/WBW layout.
    scrollAyahIntoViewSettled(key, { instant, preferTopRatio: 0.22 });
  }

  function verseAtPosition(posMs) {
    const list = timings?.verseTimings;
    if (!list?.length || !Number.isFinite(posMs)) return null;

    // Binary search — verse windows are ordered by timestamp_from
    let lo = 0;
    let hi = list.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const v = list[mid];
      if (posMs < v.timestampFrom) hi = mid - 1;
      else if (posMs >= v.timestampTo && mid < list.length - 1) lo = mid + 1;
      else return v;
    }
    return list[Math.max(0, Math.min(list.length - 1, lo))] || null;
  }

  function publishChapterAyah(verseKey, { scroll = false } = {}) {
    if (!verseKey) return;
    const changed = verseKey !== ayahKey;
    ayahKey = verseKey;
    if (mode !== 'chapter') return;
    deps.setCurrentlyPlayingAyah({
      ayahKey: verseKey,
      audioRef: audio,
      setIsPlaying: setUiPlaying || (() => {}),
    });
    if (changed) refreshMediaMetadata();
    if (scroll && changed) scrollToAyah(verseKey, { instant: true });
  }

  function adjacentVerseKey(offset) {
    const list = timings?.verseTimings;
    if (!list?.length) return null;
    const key =
      ayahKey || verseAtPosition((audio.currentTime || 0) * 1000)?.verseKey;
    if (!key) return null;
    const idx = list.findIndex((v) => v.verseKey === key);
    if (idx < 0) return null;
    return list[idx + offset]?.verseKey || null;
  }

  /** Next verse key after a given ayah key (from chapter timings). */
  function nextKeyAfter(key) {
    const list = timings?.verseTimings;
    if (!list?.length || !key) return null;
    const idx = list.findIndex((v) => v.verseKey === key);
    if (idx < 0) return null;
    return list[idx + 1]?.verseKey || null;
  }

  async function skipTrack(offset) {
    if (!timings) return;

    // Restart current ayah if >3s in (standard media-key behavior)
    if (offset < 0) {
      const curKey =
        ayahKey || verseAtPosition((audio.currentTime || 0) * 1000)?.verseKey;
      const verse = curKey ? verseFor(curKey) : null;
      if (verse) {
        const into = (audio.currentTime || 0) * 1000 - verse.timestampFrom;
        if (into > 3000) {
          if (mode === 'ayah') {
            await seekAndPlay(ayahStartMs ?? verse.timestampFrom);
          } else {
            await seekAndPlay(verse.timestampFrom);
            publishChapterAyah(verse.verseKey, { scroll: true });
          }
          syncMediaSessionUi({ forcePosition: true });
          notify();
          return;
        }
      }
    }

    const key = adjacentVerseKey(offset);
    if (!key) return;

    if (mode === 'ayah') {
      const list = timings.verseTimings || [];
      const idx = list.findIndex((v) => v.verseKey === key);
      const nextKey = idx >= 0 ? list[idx + 1]?.verseKey || null : null;
      await playAyah(key, {
        nextAyahKey: nextKey,
        setIsPlaying: setUiPlaying,
      });
      scrollToAyah(key, { instant: false });
      return;
    }

    const verse = verseFor(key);
    if (!verse) return;
    if (mode !== 'chapter') {
      mode = 'chapter';
      startChapterSync();
    }
    await seekAndPlay(verse.timestampFrom);
    publishChapterAyah(key, { scroll: true });
    updateWordHighlight();
    refreshMediaMetadata();
    syncMediaSessionUi({ forcePosition: true });
    notify();
  }

  /** Public: skip to previous (-1) or next (+1) ayah. */
  function skipAyah(offset) {
    return skipTrack(offset);
  }

  function ensureMediaSessionHandlers() {
    if (mediaHandlersBound) return;
    mediaHandlersBound = true;
    bindMediaSessionHandlers({
      play: () => {
        resume().catch(() => {});
      },
      pause: () => {
        pause();
      },
      stop: () => {
        stop();
      },
      seekbackward: (details) => {
        const offset = (details?.seekOffset || 10) * 1000;
        seekToMs((audio.currentTime || 0) * 1000 - offset);
        if (audio.paused) {
          /* keep paused */
        } else {
          syncMediaSessionUi({ forcePosition: true });
        }
      },
      seekforward: (details) => {
        const offset = (details?.seekOffset || 10) * 1000;
        seekToMs((audio.currentTime || 0) * 1000 + offset);
        syncMediaSessionUi({ forcePosition: true });
      },
      seekto: (details) => {
        if (details?.seekTime == null) return;
        seekToMs(details.seekTime * 1000);
        syncMediaSessionUi({ forcePosition: true });
      },
      previoustrack: () => {
        skipTrack(-1).catch(() => {});
      },
      nexttrack: () => {
        skipTrack(1).catch(() => {});
      },
    });
  }

  function verseFor(key) {
    return timings?.verseTimings?.find((v) => v.verseKey === key) || null;
  }

  function updateWordHighlight() {
    const posMs = audio.currentTime * 1000;
    const word =
      mode === 'ayah' && ayahKey
        ? resolveActiveWordLocation(timings, posMs, ayahKey)
        : resolveActiveWordLocation(timings, posMs);
    deps.setActiveWordLocation(word);
  }

  function settleSeek(gen) {
    if (gen !== seekGen) {
      dbg('settleSeek ignored stale gen', gen, 'current', seekGen);
      return;
    }
    const intended = pendingSeekMs;
    let posMs = audio.currentTime * 1000;
    // Browsers sometimes report pre-seek currentTime, or a concurrent ayah seek
    // yank the playhead — trust the scrubber target when they disagree.
    if (intended != null && Math.abs(posMs - intended) > 500) {
      dbg('settleSeek correcting drift', {
        reported: Math.round(posMs),
        intended: Math.round(intended),
      });
      try {
        audio.currentTime = intended / 1000;
      } catch {
        /* ignore */
      }
      posMs = intended;
    }
    pendingSeekMs = null;
    const verse = verseAtPosition(posMs);
    dbg('settleSeek', {
      gen,
      posMs: Math.round(posMs),
      verse: verse?.verseKey,
      prevAyah: ayahKey,
      mode,
    });
    isSeeking = false;
    // Scrubber always settles into chapter mode at the landed ayah
    if (mode === 'chapter' && verse) {
      publishChapterAyah(verse.verseKey, { scroll: true });
    }
    updateWordHighlight();
    notify();
  }

  /**
   * Jump to an absolute chapter position once (no intermediate ayah scrolls / reloads).
   * Sticky scrubber seeks the chapter timeline — always switches to chapter mode so
   * ayah-mode auto-advance does not walk 10:3 → 10:4 → … after a long seek.
   * @param {number} ms
   */
  function seekToMs(ms) {
    if (!Number.isFinite(ms)) return;
    const durMs =
      (Number.isFinite(audio.duration) && audio.duration > 0
        ? audio.duration * 1000
        : timings?.durationMs) || null;
    let targetMs = Math.max(0, ms);
    if (durMs != null) targetMs = Math.min(targetMs, durMs);

    const gen = ++seekGen;
    isSeeking = true;
    if (seekSettleTimer) {
      clearTimeout(seekSettleTimer);
      seekSettleTimer = null;
    }

    // Leaving per-ayah playback: scrubber owns the chapter timeline
    if (mode === 'ayah') {
      dbg('seekToMs: switching ayah → chapter mode');
      if (setUiPlaying) {
        try {
          setUiPlaying(false);
        } catch {
          /* ignore */
        }
        setUiPlaying = null;
      }
      mode = 'chapter';
      nextAyahKey = null;
      ayahStartMs = null;
      ayahEndMs = null;
      iteration = 0;
      transitioning = false;
      startChapterSync();
    } else if (mode !== 'chapter' && timings) {
      mode = 'chapter';
      startChapterSync();
    }

    const targetVerse = verseAtPosition(targetMs);
    pendingSeekMs = targetMs;
    dbg('seekToMs', {
      gen,
      targetMs: Math.round(targetMs),
      targetVerse: targetVerse?.verseKey,
      src: audio.currentSrc || audio.src,
      loadedKey,
      readyState: audio.readyState,
      mode,
    });

    try {
      audio.currentTime = targetMs / 1000;
    } catch (e) {
      dbg('seekToMs set currentTime failed', e);
    }

    // Do NOT update currentlyPlayingAyah / scroll until settle — prevents ayah walk
    notify();

    // Fallback if 'seeked' never fires (some browsers when already at position)
    seekSettleTimer = setTimeout(() => settleSeek(gen), 120);
  }

  function seekToFraction(fraction) {
    const dur =
      (Number.isFinite(audio.duration) && audio.duration > 0
        ? audio.duration
        : (timings?.durationMs || 0) / 1000) || 0;
    if (dur <= 0) {
      dbg('seekToFraction aborted — duration unknown', {
        audioDuration: audio.duration,
        timingsDuration: timings?.durationMs,
      });
      return;
    }
    const pct = Math.min(1, Math.max(0, Number(fraction) || 0));
    dbg('seekToFraction', { pct, dur });
    seekToMs(pct * dur * 1000);
  }

  function sameAudioUrl(url) {
    if (!url) return false;
    const cur = audio.currentSrc || audio.src || '';
    if (!cur) return false;
    if (cur === url) return true;
    // Browsers absolutize src — compare path tail
    try {
      const a = new URL(url, window.location.href);
      const b = new URL(cur, window.location.href);
      return a.pathname === b.pathname && a.search === b.search;
    } catch {
      return cur.includes(url) || url.includes(cur);
    }
  }

  async function ensureChapter(surahId) {
    const rid = Number(deps.getRecitationId()) || 7;
    const sid = Number(surahId);
    const key = `${rid}:${sid}`;

    if (loadedKey === key && timings?.audioUrl && sameAudioUrl(timings.audioUrl)) {
      dbg('ensureChapter CACHE HIT — no reload', key, {
        readyState: audio.readyState,
        currentTime: audio.currentTime,
      });
      return timings;
    }

    dbg('ensureChapter LOAD', { key, prevLoadedKey: loadedKey, stack: new Error().stack?.split('\n').slice(0, 6) });

    const data = await fetchChapterAudioTimings(rid, sid);
    if (!data?.audioUrl) {
      throw new Error(`No chapter audio for surah ${sid}`);
    }

    // Only touch src/load when the URL actually changes
    if (!sameAudioUrl(data.audioUrl)) {
      dbg('ensureChapter setting audio.src + load()', data.audioUrl);
      audio.src = data.audioUrl;
      audio.load();
    } else {
      dbg('ensureChapter URL already set — skip audio.load()');
    }

    timings = data;
    loadedKey = key;
    notify();
    return data;
  }

  function waitCanPlay() {
    if (audio.readyState >= 2) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const onReady = () => {
        cleanup();
        resolve();
      };
      const onErr = () => {
        cleanup();
        reject(new Error('Audio failed to load'));
      };
      const cleanup = () => {
        audio.removeEventListener('canplay', onReady);
        audio.removeEventListener('error', onErr);
      };
      audio.addEventListener('canplay', onReady, { once: true });
      audio.addEventListener('error', onErr, { once: true });
    });
  }

  /**
   * Pause → seek → wait for seeked → play.
   * Pausing first prevents the classic "hear next ayah start, then snap back
   * and hear it again" glitch when auto-advancing between verse windows.
   */
  async function seekAndPlay(ms) {
    await waitCanPlay();
    const target = Math.max(0, ms / 1000);

    try {
      if (!audio.paused) audio.pause();
    } catch {
      /* ignore */
    }

    const alreadyThere = Math.abs((audio.currentTime || 0) - target) < 0.035;
    if (!alreadyThere) {
      await new Promise((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          audio.removeEventListener('seeked', onSeeked);
          window.clearTimeout(fallback);
          resolve();
        };
        const onSeeked = () => finish();
        audio.addEventListener('seeked', onSeeked);
        const fallback = window.setTimeout(finish, 280);
        try {
          audio.currentTime = target;
        } catch {
          finish();
        }
      });
      // Some engines report seeked before currentTime sticks — nudge once.
      if (Math.abs((audio.currentTime || 0) - target) > 0.08) {
        try {
          audio.currentTime = target;
        } catch {
          /* ignore */
        }
      }
    }

    await safePlay();
  }

  /** Same-surah consecutive ayah (Android isContiguous) — stream continues, no seek. */
  function isContiguousAyahKey(fromKey, toKey) {
    if (!fromKey || !toKey) return false;
    const [s1, a1] = String(fromKey).split(':').map(Number);
    const [s2, a2] = String(toKey).split(':').map(Number);
    return (
      Number.isFinite(s1) &&
      Number.isFinite(a1) &&
      s1 === s2 &&
      a2 === a1 + 1
    );
  }

  /**
   * Advance ayah-mode playback to the next verse window.
   * Mirrors Android QuranMediaController:
   * - contiguous next ayah → update windows/UI only (no seek) to avoid double-play
   * - non-contiguous / gap → seek to next timestampFrom
   */
  async function advanceToNextAyah() {
    if (transitioning) return;
    transitioning = true;

    const finishedKey = ayahKey;
    const durationSec =
      ayahStartMs != null && ayahEndMs != null
        ? (ayahEndMs - ayahStartMs) / 1000
        : 0;
    if (finishedKey) deps.trackAyahPlayed(finishedKey, durationSec);

    const nextKey = nextAyahKey || nextKeyAfter(finishedKey);
    const followingKey = nextKey ? nextKeyAfter(nextKey) : null;

    if (!nextKey) {
      stopSync();
      try {
        audio.pause();
      } catch {
        /* ignore */
      }
      transitioning = false;
      clearPlaybackState();
      return;
    }

    const verse = verseFor(nextKey);
    if (!verse) {
      stopSync();
      try {
        audio.pause();
      } catch {
        /* ignore */
      }
      transitioning = false;
      clearPlaybackState();
      return;
    }

    // Clear prior ayah-button UI callback; context drives the new active ayah
    if (setUiPlaying) {
      try {
        setUiPlaying(false);
      } catch {
        /* ignore */
      }
      setUiPlaying = null;
    }

    const contiguous = isContiguousAyahKey(finishedKey, nextKey);

    if (contiguous) {
      // Android: if (!isContiguous) seekTo(next.from) — contiguous does NOT seek
      dbg('advance contiguous — no seek', finishedKey, '→', nextKey);
      ayahKey = nextKey;
      nextAyahKey = followingKey;
      ayahStartMs = verse.timestampFrom;
      ayahEndMs = verse.timestampTo;
      iteration = 0;
      deps.setCurrentlyPlayingAyah({
        ayahKey: nextKey,
        audioRef: audio,
        setIsPlaying: () => {},
      });
      refreshMediaMetadata();
      scrollToAyah(nextKey, { instant: false });
      updateWordHighlight();
      transitioning = false;
      if (!syncTimer && mode === 'ayah') startAyahSync();
      syncMediaSessionUi({ forcePosition: true });
      notify();
      return;
    }

    // Gap / non-contiguous queue jump — seek like Android
    dbg('advance non-contiguous — seek', finishedKey, '→', nextKey);
    stopSync();
    try {
      await playAyah(nextKey, {
        nextAyahKey: followingKey,
        setIsPlaying: null,
      });
    } catch (e) {
      console.error('advanceToNextAyah failed:', e);
      transitioning = false;
      clearPlaybackState();
    }
  }

  function startAyahSync() {
    stopSync();
    syncTimer = setInterval(() => {
      if (audio.paused || mode !== 'ayah' || ayahEndMs == null) return;
      if (isSeeking || transitioning) return;

      updateWordHighlight();

      const posMs = audio.currentTime * 1000;
      // Android: pos >= currentTiming.timestampTo (half-open window)
      if (posMs >= ayahEndMs) {
        // If playback position jumped far past this ayah (stale sync after seek),
        // snap to the verse at the current position instead of walking one-by-one.
        const at = verseAtPosition(posMs);
        if (at && at.verseKey !== ayahKey) {
          const expectedNext = nextAyahKey;
          const isAdjacent =
            expectedNext && at.verseKey === expectedNext;
          if (!isAdjacent) {
            dbg('ayahSync far jump detected → chapter mode', {
              from: ayahKey,
              at: at.verseKey,
              posMs: Math.round(posMs),
            });
            if (setUiPlaying) {
              try {
                setUiPlaying(false);
              } catch {
                /* ignore */
              }
              setUiPlaying = null;
            }
            mode = 'chapter';
            ayahStartMs = null;
            ayahEndMs = null;
            nextAyahKey = null;
            startChapterSync();
            publishChapterAyah(at.verseKey, { scroll: true });
            notify();
            return;
          }
        }

        iteration += 1;
        const loops = deps.getLoopCount();
        if (iteration < loops) {
          // Repeat same ayah — Android seeks back to timestampFrom
          stopSync();
          seekAndPlay(ayahStartMs)
            .then(() => {
              if (mode === 'ayah') startAyahSync();
              notify();
            })
            .catch(() => {});
          return;
        }
        // Contiguous advance keeps the stream playing (no stopSync/pause here)
        advanceToNextAyah().catch((e) => console.error(e));
        return;
      }
      notify();
    }, SYNC_MS);
  }

  function startChapterSync() {
    stopSync();
    syncTimer = setInterval(() => {
      if (audio.paused || mode !== 'chapter') return;

      // While seeking: only refresh progress snapshot — never ayah/scroll
      if (isSeeking) {
        notify();
        return;
      }

      const posMs = audio.currentTime * 1000;
      const verse = verseAtPosition(posMs);
      if (verse && verse.verseKey !== ayahKey) {
        dbg('chapterSync ayah change', ayahKey, '->', verse.verseKey, Math.round(posMs));
        publishChapterAyah(verse.verseKey, { scroll: true });
      }
      updateWordHighlight();
      notify();
    }, SYNC_MS);
  }

  async function playAyah(key, { nextAyahKey: nextKey = null, setIsPlaying = null } = {}) {
    dbg('playAyah', key);
    transitioning = true;
    stopSync();
    try {
      const sid = Number(String(key).split(':')[0]);
      await ensureChapter(sid);

      const verse = verseFor(key);
      if (!verse) throw new Error(`No timing for ${key}`);

      if (setUiPlaying && setUiPlaying !== setIsPlaying) {
        try {
          setUiPlaying(false);
        } catch {
          /* ignore */
        }
      }

      mode = 'ayah';
      ayahKey = key;
      // Fill next from timings when caller omitted it (keeps auto-advance chain alive)
      nextAyahKey = nextKey != null ? nextKey : nextKeyAfter(key);
      ayahStartMs = verse.timestampFrom;
      ayahEndMs = verse.timestampTo;
      iteration = 0;
      setUiPlaying = setIsPlaying;

      deps.setCurrentlyPlayingAyah({
        ayahKey: key,
        audioRef: audio,
        setIsPlaying: setIsPlaying || (() => {}),
      });

      if (setIsPlaying) setIsPlaying(true);
      ensureMediaSessionHandlers();
      refreshMediaMetadata();
      // Seek first, then start sync — otherwise sync can see the old playhead
      // still inside the previous verse / early next verse and mis-fire.
      await seekAndPlay(ayahStartMs);
      transitioning = false;
      startAyahSync();
      updateWordHighlight();
      syncMediaSessionUi({ forcePosition: true });
      notify();
    } catch (e) {
      transitioning = false;
      throw e;
    }
  }

  async function playChapter(surahId, fromMs = 0) {
    dbg('playChapter', surahId, fromMs);
    transitioning = true;
    stopSync();
    if (setUiPlaying) {
      try {
        setUiPlaying(false);
      } catch {
        /* ignore */
      }
      setUiPlaying = null;
    }
    try {
      await ensureChapter(surahId);
      mode = 'chapter';
      ayahKey = null;
      nextAyahKey = null;
      ayahStartMs = null;
      ayahEndMs = null;
      iteration = 0;
      deps.setCurrentlyPlayingAyah(null);
      ensureMediaSessionHandlers();
      await seekAndPlay(fromMs);
      transitioning = false;
      startChapterSync();
      const verse = verseAtPosition(fromMs);
      if (verse) publishChapterAyah(verse.verseKey, { scroll: true });
      else refreshMediaMetadata();
      updateWordHighlight();
      syncMediaSessionUi({ forcePosition: true });
      notify();
    } catch (e) {
      transitioning = false;
      throw e;
    }
  }

  function pause() {
    dbg('pause');
    audio.pause();
    clearPlaybackBlocked();
    if (setUiPlaying) {
      try {
        setUiPlaying(false);
      } catch {
        /* ignore */
      }
    }
    deps.setActiveWordLocation(null);
    setMediaSessionPlaybackState('paused');
    syncMediaSessionUi({ forcePosition: true });
    notify();
  }

  function resume() {
    dbg('resume');
    if (!timings) return Promise.resolve();
    ensureMediaSessionHandlers();
    return safePlay().then(() => {
      if (mode === 'ayah') startAyahSync();
      if (mode === 'chapter') startChapterSync();
      if (setUiPlaying) setUiPlaying(true);
      if (ayahKey) {
        deps.setCurrentlyPlayingAyah({
          ayahKey,
          audioRef: audio,
          setIsPlaying: setUiPlaying || (() => {}),
        });
      }
      refreshMediaMetadata();
      setMediaSessionPlaybackState('playing');
      syncMediaSessionUi({ forcePosition: true });
      notify();
    });
  }

  function stop() {
    dbg('stop');
    audio.pause();
    clearPlaybackBlocked();
    clearPlaybackState();
  }

  function onRecitationChange() {
    dbg('onRecitationChange — clearing media');
    stop();
    loadedKey = '';
    timings = null;
    audio.removeAttribute('src');
    audio.load();
    clearChapterAudioTimingsCache();
    notify();
  }

  function subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  }

  audio.addEventListener('ended', () => {
    if (mode === 'chapter') {
      clearPlaybackState();
    } else if (mode === 'ayah') {
      advanceToNextAyah().catch(() => clearPlaybackState());
    }
  });

  audio.addEventListener('pause', () => notify());
  audio.addEventListener('play', () => notify());

  audio.addEventListener('loadstart', () => {
    dbg('event loadstart', { src: audio.currentSrc || audio.src, loadedKey });
  });
  audio.addEventListener('loadedmetadata', () => {
    dbg('event loadedmetadata', { duration: audio.duration, loadedKey });
  });

  audio.addEventListener('seeking', () => {
    isSeeking = true;
    dbg('event seeking', {
      t: audio.currentTime,
      seekGen,
      ayahKey,
    });
  });

  audio.addEventListener('seeked', () => {
    const gen = seekGen;
    dbg('event seeked', {
      t: audio.currentTime,
      gen,
      verse: verseAtPosition(audio.currentTime * 1000)?.verseKey,
    });
    if (seekSettleTimer) clearTimeout(seekSettleTimer);
    // Coalesce rapid intermediate seeked events into one settle
    seekSettleTimer = setTimeout(() => settleSeek(gen), 80);
  });

  return {
    audio,
    ensureChapter,
    playAyah,
    playChapter,
    pause,
    resume,
    stop,
    seekToMs,
    seekToFraction,
    skipAyah,
    onRecitationChange,
    subscribe,
    getSnapshot,
    getTimings: () => timings,
    getAudio: () => audio,
  };
}
